/* Animated proof: one plate, three steps, three bars, shared by every persona.
   Documented in kg-custom-sections.md § animated-proof.

   Fig. 1 plays across steps 1 and 2, Fig. 2 on step 3, with a crossfade between them. The figure is a
   module from window.KGFigures, chosen by the plate's data-animation (the block's animation setting):
   module(plate) returns { fig1, fig2, segments: [[a, b], [a, b], [a, b]], still: [t1, t2] }, where each
   fig has .svg and .render(t).

   The block on show follows the one persona rule (window.kgPersona from snippets/kg-persona.liquid),
   ranked as comment-plate ranks its lead. The Liquid renders the default; this re-chooses for ?ref.

   QA switches, inert without the query: ?t=<seconds> freezes the plate at that time; ?still=1 shows
   the reduced-motion state. */
(function () {
  'use strict';
  if (customElements.get('animated-proof')) return;

  var CROSSFADE_SECONDS = 0.5;
  var LABEL_SWAP_MS = 260;
  var MAX_FRAME_STEP_SECONDS = 0.1;   // a backgrounded tab resumes without jumping
  var VISIBLE_SHARE = 0.35;
  var SWIPE_MIN_PX = 40;
  var SWIPE_DOMINANCE = 1.5;          // horizontal travel must beat vertical travel by this factor
  var LAST_STEP = 2;
  // A tap lands a hair inside the step rather than on its boundary, so the step shown is the one tapped
  // and its bar starts filling from the first frame.
  var TAP_LANDING_SECONDS = 0.001;

  var carousels = new WeakMap();

  function smoothstep(u) { u = u < 0 ? 0 : u > 1 ? 1 : u; return u * u * (3 - 2 * u); }
  function prefersStill() {
    if (/[?&]still=1/.test(location.search)) return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function frozenTime() { var match = /[?&]t=([\d.]+)/.exec(location.search); return match ? +match[1] : null; }
  function requireElement(root, selector) {
    var node = root.querySelector(selector);
    if (!node) throw new Error('Animated proof: missing ' + selector);
    return node;
  }
  function leadRank(block, persona, pageHandle) {
    var rank = block.dataset.leadFallback === 'true' ? 1 : 0;
    if (!persona || block.dataset.leadFor !== persona) return rank;
    var pages = (block.dataset.leadPages || '').split(',').map(function (p) { return p.trim(); }).filter(Boolean);
    if (pages.length === 0) return 2;
    return pages.indexOf(pageHandle) !== -1 ? 3 : rank;
  }

  function FigureCarousel(block) {
    var plate = requireElement(block, '[data-animation]');
    var mountFigure = window.KGFigures && window.KGFigures[plate.dataset.animation];
    if (!mountFigure) throw new Error('Animated proof: no figure module named "' + plate.dataset.animation + '"');
    this.block = block;
    this.plate = plate;
    this.module = mountFigure(plate);
    this.steps = block.querySelectorAll('[data-ap-step]');
    this.bars = [].slice.call(block.querySelectorAll('[data-ap-bar]'));
    this.barFills = this.bars.map(function (bar) { return requireElement(bar, '[data-ap-bar-fill]'); });
    this.label = requireElement(block, '[data-ap-label]');
    this.labelNumber = requireElement(this.label, '[data-ap-label-number]');
    this.labelName = requireElement(this.label, '[data-ap-label-name]');
    this.labelTexts = this.readLabelTexts();
    this.announcer = requireElement(block, '[data-ap-announce]');
    this.measureSegments();
    this.tau = 0; this.last = null; this.raf = 0; this.visible = false; this.shown = -1; this.labelFig = 1;
    this.start();
  }

  FigureCarousel.prototype.readLabelTexts = function () {
    var staticLabel = requireElement(this.block, '[data-ap-label-static]');
    return [
      { number: this.labelNumber.textContent, name: this.labelName.textContent },
      { number: requireElement(staticLabel, '[data-ap-label-number]').textContent, name: requireElement(staticLabel, '[data-ap-label-name]').textContent }
    ];
  };

  FigureCarousel.prototype.measureSegments = function () {
    var starts = [], total = 0;
    this.module.segments.forEach(function (segment) { starts.push(total); total += segment[1] - segment[0]; });
    this.starts = starts;
    this.total = total;
  };

  FigureCarousel.prototype.start = function () {
    if (prefersStill()) { this.showStill(); return; }
    var self = this;
    this.bars.forEach(function (bar, n) { bar.addEventListener('click', function () { self.goTo(n); }); });
    this.listenForSwipes();
    var frozen = frozenTime();
    if (frozen !== null) { this.instantLabel = true; this.tau = frozen % this.total; this.last = 0; this.render(); return; }
    this.render();
    this.playWhileVisible();
  };

  FigureCarousel.prototype.showStill = function () {
    var figures = this.module;
    this.block.classList.add('ap--static');
    figures.fig1.render(figures.still[0]);
    figures.fig2.render(figures.still[1]);
    figures.fig1.svg.style.opacity = 1;
    figures.fig2.svg.style.opacity = 1;
  };

  FigureCarousel.prototype.playWhileVisible = function () {
    var self = this;
    new IntersectionObserver(function (entries) {
      self.visible = entries[0].isIntersecting;
      if (self.visible && !self.raf) { self.last = null; self.raf = requestAnimationFrame(self.frame.bind(self)); }
    }, { threshold: VISIBLE_SHARE }).observe(this.plate);
  };

  FigureCarousel.prototype.goTo = function (n) {
    this.tau = this.starts[n] + TAP_LANDING_SECONDS;
    if (!this.raf) this.render();
    this.announcer.textContent = this.steps[n].textContent;   // speak only on a tap or swipe, never on auto-advance
  };

  // Swipe left for the next step, right for the previous one. Vertical scrolling is left alone
  // (touch-action: pan-y in the CSS); the last step swipes on to the first, as the loop does.
  FigureCarousel.prototype.listenForSwipes = function () {
    var self = this, from = null;
    this.block.addEventListener('touchstart', function (event) {
      from = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    this.block.addEventListener('touchend', function (event) {
      if (!from) return;
      var dx = event.changedTouches[0].clientX - from.x, dy = event.changedTouches[0].clientY - from.y;
      from = null;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE) return;
      if (dx < 0) self.goTo((self.shown + 1) % self.steps.length);
      else if (self.shown > 0) self.goTo(self.shown - 1);
    }, { passive: true });
  };

  FigureCarousel.prototype.frame = function (now) {
    if (this.last !== null) this.tau = (this.tau + Math.min(MAX_FRAME_STEP_SECONDS, (now - this.last) / 1000)) % this.total;
    this.last = now;
    this.render();
    this.raf = this.visible ? requestAnimationFrame(this.frame.bind(this)) : 0;
  };

  FigureCarousel.prototype.position = function () {
    var starts = this.starts, tau = this.tau;
    var index = tau < starts[1] ? 0 : tau < starts[2] ? 1 : 2;
    var segment = this.module.segments[index];
    return { index: index, segment: segment, elapsed: tau - starts[index] };
  };

  FigureCarousel.prototype.render = function () {
    var at = this.position();
    this.renderFigures(at);
    this.showStep(at.index);
    this.setLabel(at.index === LAST_STEP ? 2 : 1);
    this.renderBars(at);
  };

  FigureCarousel.prototype.renderFigures = function (at) {
    var figures = this.module;
    var fig = at.index === LAST_STEP ? figures.fig2 : figures.fig1;
    fig.render(at.segment[0] + at.elapsed);
    if (at.index === LAST_STEP) {
      var fadeIn = smoothstep(at.elapsed / CROSSFADE_SECONDS);
      figures.fig2.svg.style.opacity = fadeIn;
      figures.fig1.svg.style.opacity = 1 - fadeIn;
      if (fadeIn < 1) figures.fig1.render(figures.segments[1][1]);
    } else if (at.index === 0 && at.elapsed < CROSSFADE_SECONDS && this.last !== null) {
      var back = smoothstep(at.elapsed / CROSSFADE_SECONDS);
      figures.fig1.svg.style.opacity = back;
      figures.fig2.svg.style.opacity = 1 - back;
      figures.fig2.render(figures.segments[2][1]);
    } else {
      figures.fig1.svg.style.opacity = 1;
      figures.fig2.svg.style.opacity = 0;
    }
  };

  FigureCarousel.prototype.showStep = function (index) {
    if (index === this.shown) return;
    this.shown = index;
    for (var n = 0; n < this.steps.length; n++) this.steps[n].classList.toggle('is-on', n === index);
  };

  FigureCarousel.prototype.renderBars = function (at) {
    var share = at.elapsed / (at.segment[1] - at.segment[0]);
    var fills = this.barFills;
    this.bars.forEach(function (bar, n) {
      var filled = n < at.index ? 1 : n > at.index ? 0 : share;
      fills[n].style.transform = 'scaleX(' + filled.toFixed(4) + ')';
      bar.setAttribute('aria-current', n === at.index ? 'step' : 'false');
    });
  };

  FigureCarousel.prototype.setLabel = function (figNumber) {
    if (this.labelFig === figNumber) return;
    this.labelFig = figNumber;
    var label = this.label, number = this.labelNumber, name = this.labelName, text = this.labelTexts[figNumber - 1];
    function swap() {
      number.textContent = text.number;
      name.textContent = text.name;
      label.classList.toggle('is-gold', figNumber === 2);
      label.classList.remove('is-out');
    }
    if (this.instantLabel) { label.style.transition = 'none'; swap(); return; }   // a frozen ?t frame shows the settled label
    label.classList.add('is-out');
    setTimeout(swap, LABEL_SWAP_MS);
  };

  // Mounted lazily, once per block, when the persona rule shows that block.
  function mountCarousel(block) {
    if (!carousels.has(block)) carousels.set(block, new FigureCarousel(block));
  }

  // Deferred scripts run in document order, so a second instance's figure module (emitted after the
  // first instance's controller tag) can still be loading when this element connects. Wait for it.
  function whenFiguresRegistered(root, callback) {
    var names = [].slice.call(root.querySelectorAll('[data-animation]')).map(function (plate) { return plate.dataset.animation; });
    var tries = 0;
    (function check() {
      var ready = names.every(function (name) { return window.KGFigures && window.KGFigures[name]; });
      if (ready || tries++ > 200) return callback();
      setTimeout(check, 25);
    })();
  }

  class AnimatedProof extends HTMLElement {
    connectedCallback() {
      this.blocks = [].slice.call(this.querySelectorAll('[data-ap-block]'));
      whenFiguresRegistered(this, this.showLead.bind(this));
      this.onSectionLoad = this.showLead.bind(this);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
    }

    activePersona() {
      var preview = this.dataset.preview;
      if (window.Shopify && Shopify.designMode && preview) return preview;
      return (window.kgPersona && window.kgPersona.active) || '';
    }

    chooseLead() {
      var persona = this.activePersona();
      var pageHandle = this.dataset.page || '';
      var best = null;
      var bestRank = -1;
      this.blocks.forEach(function (block) {
        var rank = leadRank(block, persona, pageHandle);
        if (rank > bestRank) { best = block; bestRank = rank; }
      });
      return best;
    }

    showLead() {
      var lead = this.chooseLead();
      this.blocks.forEach(function (block) { block.hidden = block !== lead; });
      if (lead) mountCarousel(lead);
    }
  }

  customElements.define('animated-proof', AnimatedProof);
})();
